import React, { useMemo, useState, useCallback } from "react"
import { Link } from "react-router-dom"
import * as Yup from "yup"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import {
  Col,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
  Button,
} from "reactstrap"

// Custom Components
import TableContainer from "../../../components/Common/TableContainer"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import DeleteModal from "../../../components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"

// Custom Hooks
import {
  useCreateYear,
  useDeleteYear,
  useEditYear,
  useYears,
} from "hooks/yearHook"
import { useListParams } from "hooks/useListParams"

// Utils
import { formatReadableDate } from "utils/formatDate"

const YearList = () => {
  document.title = "Year List"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [year, setYear] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({ page: 1, search: "" })

  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: 10,
      ...(params.search && { search: params.search }),
    }),
    [params.page, params.search]
  )

  const {
    data: yearsResponse,
    isLoading,
    refetch: yearRefetch,
    isRefetching: yearRefetchLoading,
  } = useYears(queryParams)

  const { mutateAsync: createYear, isPending: yearCreateLoading } =
    useCreateYear()
  const { mutateAsync: editYear, isPending: yearEditLoading } = useEditYear()
  const { mutateAsync: deleteYear, isPending: yearDeleteLoading } =
    useDeleteYear()

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false)
      setYear(null)
      setIsEdit(false)
    } else {
      setModal(true)
    }
  }, [modal])

  const handleAddNewClick = useCallback(() => {
    setYear(null)
    setIsEdit(false)
    toggle()
  }, [toggle])

  const handleYearClick = useCallback(
    (arg) => {
      setYear({ id: arg.id, name: arg.name })
      setIsEdit(true)
      toggle()
    },
    [toggle]
  )

  const onClickDelete = useCallback((yearData) => {
    setYear(yearData)
    setDeleteModal(true)
  }, [])

  const handleDeleteYear = useCallback(async () => {
    if (year?.id) {
      try {
        const res = await deleteYear(year.id)
        toast.success(res?.message ?? "Year deleted successfully.")
        setDeleteModal(false)
      } catch (error) {
        console.error("Failed to delete year:", error)
        toast.error("Failed to delete year.")
      }
    }
  }, [year, deleteYear])

  const handleYearListRefresh = () => {
    yearRefetch()
  }

  // Form validation
  const validationSchema = Yup.object({
    name: Yup.number()
      .required("Please enter a year")
      .min(1900, "Year must be 1900 or later")
      .max(2100, "Year must be 2100 or earlier"),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = { name: parseInt(values.name) }
      const action = isEdit
        ? editYear({ id: year.id, ...payload })
        : createYear(payload)

      try {
        const res = await action
        toast.success(
          res?.message ?? `Year ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggle()
      } catch (error) {
        console.error("Failed to save year:", error)
        toast.error("Failed to save year.")
      }
    },
    [isEdit, year, editYear, createYear, toggle]
  )

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { name: year?.name || "" },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        cell: ({ row }) => (
          <Link to="#" className="text-body fw-bold">
            {row.original.id}
          </Link>
        ),
      },
      {
        header: "Year",
        accessorKey: "name",
      },
      {
        header: "Slug",
        accessorKey: "slug",
      },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-info"
                onClick={() => handleYearClick(row.original)}
                id={`edittooltip-${row.original.id}`}
              >
                <i className="mdi mdi-pencil-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`edittooltip-${row.original.id}`}
                >
                  Edit
                </UncontrolledTooltip>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-danger"
                onClick={() => onClickDelete(row.original)}
                id={`deletetooltip-${row.original.id}`}
              >
                <i className="mdi mdi-delete-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`deletetooltip-${row.original.id}`}
                >
                  Delete
                </UncontrolledTooltip>
              </button>
            </li>
          </ul>
        ),
      },
    ],
    [handleYearClick, onClickDelete]
  )

  const yearsData = yearsResponse?.data?.data || []
  const paginationInfo = yearsResponse?.data

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteYear}
        loading={yearDeleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Year" breadcrumbItem="Year Lists" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom">
                  <div className="d-flex align-items-center">
                    <h5 className="mb-0 card-title flex-grow-1">Year Lists</h5>
                    <div className="flex-shrink-0">
                      <Button
                        color="primary"
                        onClick={handleAddNewClick}
                        className="me-1"
                      >
                        Add New Year
                      </Button>
                      <Button
                        color="light"
                        onClick={handleYearListRefresh}
                        className="me-1"
                        id="refreshTooltip"
                      >
                        <i
                          className={`mdi mdi-refresh ${
                            yearRefetchLoading ? "mdi-spin" : ""
                          }`}
                        />
                        <UncontrolledTooltip
                          placement="top"
                          target="refreshTooltip"
                        >
                          Refresh
                        </UncontrolledTooltip>
                      </Button>
                      {hasActiveFilters && (
                        <Button
                          color="danger"
                          onClick={resetParams}
                          className="me-1"
                          id="resetTooltip"
                        >
                          <i className="mdi mdi-close" />
                          <UncontrolledTooltip
                            placement="top"
                            target="resetTooltip"
                          >
                            Reset Filters
                          </UncontrolledTooltip>
                        </Button>
                      )}
                    </div>
                  </div>
                </CardBody>
                <CardBody>
                  <Row className="mb-3">
                    <Col sm={4}>
                      <GlobalInputSearch
                        value={params.search}
                        onChange={setSearch}
                      />
                    </Col>
                  </Row>
                  <TableContainer
                    columns={columns}
                    data={yearsData}
                    isPagination={true}
                    paginationInfo={paginationInfo}
                    onPageChange={setPage}
                    loading={isLoading || yearRefetchLoading}
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                    pagination="pagination"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Modal isOpen={modal} toggle={toggle} centered>
            <ModalHeader toggle={toggle} tag="h4">
              {isEdit ? "Edit Year" : "Add Year"}
            </ModalHeader>
            <ModalBody>
              <Form
                onSubmit={(e) => {
                  e.preventDefault()
                  validation.handleSubmit()
                }}
              >
                <div className="mb-3">
                  <Label htmlFor="name">Year</Label>
                  <Input
                    name="name"
                    id="name"
                    type="number"
                    placeholder="Enter year"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.name || ""}
                    invalid={validation.touched.name && !!validation.errors.name}
                  />
                  {validation.touched.name && validation.errors.name ? (
                    <FormFeedback type="invalid">
                      {validation.errors.name}
                    </FormFeedback>
                  ) : null}
                </div>
                <div className="text-end">
                  <Button
                    color="success"
                    type="submit"
                    disabled={yearEditLoading || yearCreateLoading}
                  >
                    {isEdit ? "Save Changes" : "Add Year"}
                    {(yearEditLoading || yearCreateLoading) && "..."}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
    </React.Fragment>
  )
}

export default YearList
